import type { Stripe, StripeCardElement, StripeError } from "@stripe/stripe-js";
import type { CardHandle, ConfirmResult, IStripeSdk } from "./types";

// StripeSdkReal wraps the official @stripe/stripe-js. Stripe.js is lazy-loaded
// inside mountCardElement so the bundle does not pull stripe.com on app boot —
// it only ships to the network when the user opens the payment step.
export class StripeSdkReal implements IStripeSdk {
  private stripe: Stripe | null = null;
  private cardElement: StripeCardElement | null = null;

  constructor(private readonly publishableKey: string) {}

  async mountCardElement(container: HTMLElement): Promise<CardHandle> {
    const { loadStripe } = await import("@stripe/stripe-js");
    const stripe = await loadStripe(this.publishableKey);
    if (!stripe) {
      throw new Error("Failed to load Stripe.js");
    }
    this.stripe = stripe;

    const elements = stripe.elements();
    const cardElement = elements.create("card");
    cardElement.mount(container);
    this.cardElement = cardElement;

    return {
      unmount: () => {
        cardElement.unmount();
        this.cardElement = null;
      },
    };
  }

  async confirmCardPayment(clientSecret: string): Promise<ConfirmResult> {
    if (!this.stripe || !this.cardElement) {
      return { status: "failed", message: "Card form not mounted" };
    }
    // PaymentIntentResult is a discriminated union — see
    // https://docs.stripe.com/js/payment_intents/confirm_card_payment :
    //   | { paymentIntent: PaymentIntent; error?: undefined }
    //   | { paymentIntent?: undefined; error: StripeError }
    const result = await this.stripe.confirmCardPayment(clientSecret, {
      payment_method: { card: this.cardElement },
    });
    if (result.error) {
      return formatStripeError(result.error);
    }
    return { status: "succeeded", paymentIntentId: result.paymentIntent.id };
  }
}

function formatStripeError(err: StripeError): ConfirmResult {
  return {
    status: "failed",
    message: err.message ?? "Payment failed",
    code: err.decline_code ?? err.code,
  };
}
